import { StyledTextFadeOverflowRolling } from '@tmh-website/components/shared/TextFadeOverflowRolling';
import { resetButtonStylingCSS } from '@tmh-website/styles/global';
import StyledTypo from '@tmh-website/styles/typo';
import rgba from 'polished/lib/color/rgba';
import transparentize from 'polished/lib/color/transparentize';
import styled, { css } from 'styled-components';
import { TagThemeColor, tagThemes } from './Tag.theme';

interface ContainerProps {
  $themeColor: TagThemeColor;
  $size: 'xSmall' | 'small' | 'large';
  $isLink: boolean;
  $hasTransparentText: boolean;
  $hasRollingContainer: boolean;
  ref?: any;
}

export const Container = styled.div<ContainerProps>`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  color: ${props =>
    props.$hasTransparentText
      ? rgba(props.theme.color[tagThemes[props.$themeColor].text], 0.6)
      : props.theme.color[tagThemes[props.$themeColor].text]};
  background-color: ${props => props.theme.color[tagThemes[props.$themeColor].background]};

  ${props =>
    props.$isLink &&
    css`
      ${resetButtonStylingCSS}
      background-color: ${props.theme.color[tagThemes[props.$themeColor].background]};
      // The background gradient of the TextFadeOverflowRolling container can't have a transition
      // Therefore we don't use it at all
      ${!props.$hasRollingContainer && `transition: background-color ${props.theme.transitionTiming};`};

      &:hover {
        background-color: ${props.theme.color[tagThemes[props.$themeColor].hover]};
        text-decoration: none;

        ${StyledTextFadeOverflowRolling.Fade}:first-of-type {
          background: linear-gradient(
            to left,
            ${transparentize(1, props.theme.color[tagThemes[props.$themeColor].hover])} 0%,
            ${props.theme.color[tagThemes[props.$themeColor].hover]} 100%
          );
        }

        ${StyledTextFadeOverflowRolling.Fade}:nth-of-type(2) {
          background: linear-gradient(
            to right,
            ${transparentize(1, props.theme.color[tagThemes[props.$themeColor].hover])} 0%,
            ${props.theme.color[tagThemes[props.$themeColor].hover]} 100%
          );
        }
      }

      &:active {
        background-color: ${props.theme.color[tagThemes[props.$themeColor].active]};
        ${StyledTextFadeOverflowRolling.Fade}:first-of-type {
          background: linear-gradient(
            to left,
            ${transparentize(1, props.theme.color[tagThemes[props.$themeColor].active])} 0%,
            ${props.theme.color[tagThemes[props.$themeColor].active]} 100%
          );
        }

        ${StyledTextFadeOverflowRolling.Fade}:nth-of-type(2) {
          background: linear-gradient(
            to right,
            ${transparentize(1, props.theme.color[tagThemes[props.$themeColor].active])} 0%,
            ${props.theme.color[tagThemes[props.$themeColor].active]} 100%
          );
        }
      }
    `}

  ${props =>
    props.$size === 'xSmall' &&
    css`
      ${StyledTypo.uiTextSmallCss};
      gap: 6px;
      border-radius: ${props.theme.isBorderRadiusDisabled ? 0 : '11px'};
      height: 24px;
      padding: 2px ${props.$hasRollingContainer ? 5 : 11}px;
    `}

  ${props =>
    props.$size === 'small' &&
    css`
      ${StyledTypo.uiTextCss};
      height: 30px;
      gap: 10px;
      border-radius: ${props.theme.isBorderRadiusDisabled ? 0 : '15px'};
      padding: 2px 16px;
    `}

  ${props =>
    props.$size === 'large' &&
    css`
      ${StyledTypo.uiTextCss};
      height: 42px;
      gap: 10px;
      border-radius: ${props.theme.isBorderRadiusDisabled ? 0 : '21px'};
      padding: 8px 24px;
    `}
`;

export const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DeleteButton = styled.button`
  ${resetButtonStylingCSS};
  padding: 8px;
  margin: -8px;
  transition: opacity ${props => props.theme.transitionTiming};

  &:hover {
    opacity: 0.6;
  }

  &:active {
    opacity: 0.4;
  }

  > svg {
    display: block;
  }
`;
